import ModuleSkeleton from "@/components/app/ModuleSkeleton";
import { useAuth } from "@/lib/auth";
import { Card } from "@/components/ui/card";
import { ShieldCheck } from "lucide-react";
import { Workflow, Bolt, GitBranch, Bell, FileSignature, Slack, Mail, ArrowRight, Database } from "lucide-react";

const TEMPLATES = [
  { name: "New Joiner Onboarding",    triggers: "When user is hired",            steps: 12, icon: GitBranch },
  { name: "Leave Approval Chain",     triggers: "When leave is requested",       steps: 4,  icon: Bell      },
  { name: "Asset Allocation",         triggers: "On day -1 of joining",          steps: 6,  icon: Bolt      },
  { name: "Payroll Cut-off Reminder", triggers: "Every 25th of the month",       steps: 3,  icon: Mail      },
  { name: "Offer Letter Generation",  triggers: "When offer is approved",        steps: 5,  icon: FileSignature },
  { name: "Slack Birthday Alert",     triggers: "Daily 9 AM",                    steps: 2,  icon: Slack     },
];

export default function WorkflowStudio() {
  const { user } = useAuth();
  if (user && !["super_admin", "hr_admin"].includes(user.role) && !user.is_platform_owner) {
    return (
      <div className="min-h-[60vh] flex items-center justify-center" data-testid="workflow-studio-denied">
        <Card className="p-10 text-center max-w-md">
          <div className="w-14 h-14 rounded-2xl bg-amber-100 text-amber-700 mx-auto mb-4 flex items-center justify-center">
            <ShieldCheck className="w-7 h-7" />
          </div>
          <div className="text-lg font-bold">Admin-only feature</div>
          <p className="text-sm text-slate-500 mt-2">Workflow Studio is available to HR Admins and Super Admins.</p>
        </Card>
      </div>
    );
  }
  return (
    <ModuleSkeleton
      testid="workflow-studio-page"
      title="Workflow Studio"
      subtitle="No-code automation for HR. Build trigger → condition → action chains across every QrewHR module."
      icon={Workflow}
      badge="Basic Version"
      tagline="Automate the boring 80%"
      cta={{ label: "Browse templates", onClick: () => document.getElementById("wf-templates")?.scrollIntoView({ behavior: "smooth" }) }}
      features={[
        { title: "Visual Builder",     description: "Drag-and-drop nodes — triggers, conditions, AI steps, actions.",  icon: Workflow   },
        { title: "100+ Action Blocks", description: "From email send to payroll trigger. Cross-module by default.",   icon: Bolt       },
        { title: "Branching Logic",    description: "If / else / wait / loop — complex flows without engineers.",      icon: GitBranch  },
        { title: "AI Steps",           description: "Drop-in Claude steps to summarise, classify, draft, decide.",     icon: ArrowRight },
        { title: "Webhooks & APIs",    description: "Call external systems, ingest webhooks, post to Slack/Teams.",    icon: Database   },
        { title: "Audit-grade Logs",   description: "Every run logged with input/output/duration for compliance.",     icon: Bell       },
      ]}
      capabilities={[
        "Trigger on any event: hire, leave, claim, ticket, schedule",
        "Approval chains with escalation timers",
        "Pre-built templates (onboarding, offboarding, claims)",
        "Versioned drafts with rollback",
        "Per-tenant rate limits + retry policies",
        "Test mode with dry-run inspection",
      ]}
    >
      <Card id="wf-templates" className="p-6 border border-slate-200 bg-white" data-testid="workflow-templates-preview">
        <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
          <div className="text-xs uppercase tracking-[0.18em] font-bold text-slate-500">Workflow templates · ready to clone</div>
          <div className="text-[11px] text-slate-400">{TEMPLATES.length} starter flows</div>
        </div>
        <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
          {TEMPLATES.map((t) => (
            <div key={t.name} className="p-4 rounded-xl bg-white border border-slate-200 hover:border-violet-300 hover:shadow-md transition cursor-pointer group" data-testid={`workflow-template-${t.name.replace(/\s+/g, "-").toLowerCase()}`}>
              <div className="flex items-start justify-between mb-2">
                <div className="w-9 h-9 rounded-lg qh-grad-bg text-white flex items-center justify-center">
                  <t.icon className="w-4 h-4" />
                </div>
                <span className="text-[10px] font-bold text-emerald-700 bg-emerald-50 px-2 py-0.5 rounded-full uppercase tracking-wider">Preview</span>
              </div>
              <div className="font-semibold text-sm text-slate-900 group-hover:text-violet-700 transition">{t.name}</div>
              <div className="text-[11px] text-slate-500 mt-1">{t.triggers}</div>
              <div className="text-[10px] uppercase tracking-wider font-bold text-slate-400 mt-2 flex items-center justify-between">
                <span>{t.steps} steps</span>
                <ArrowRight className="w-3 h-3 text-violet-500 opacity-0 group-hover:opacity-100 transition" />
              </div>
            </div>
          ))}
        </div>
      </Card>
    </ModuleSkeleton>
  );
}
